import * as LDClient from 'launchdarkly-js-client-sdk';
import { isEmpty } from 'lodash';
import ConfigService from 'util/ConfigService';
import LocalStorageService from 'util/LocalStorageService';
import Vue from 'vue';
import shajs from 'sha.js';

const localStorageOrgKey = 'login-ui-OrganizationId';
const featureFlagsModule = 'FeatureFlagsModule';

/**
 * These are the names of all feature flags that are available to the application. They are kept
 * here, in the FeatureFlagsModule so that they can be imported and used instead of repeating
 * magic strings throughout the application when feature flags are needed.
 */
export const featureFlags = {};

/**
 * This hash maps beta feature keys used by components to feature flag names used in the feature
 * management provider. It allows for the overriding of existing Mongo feature flags with
 * LaunchDarkly feature flags and the use of more succinct names for LaunchDarkly feature flags.
 */
export const featureFlagToProviderMap = {};

/**
 * Default values for feature flags will be used when the launchDarklySource is local storage. This
 * is useful in this use case so that flags that have targeting enabled and return a particular
 * default value from LD can return the same default value when working with local storage.
 */
export const featureFlagDefaultValues = {};

/**
 * Lock feature flags to only be set once. This can be helpful for feature flags that change app
 * behavior in a way that requires a full page reload to handle the change.
 * Future: Consider notifying the user that there is an update available if they refresh.
 */
const singleSetFeatureFlags = new Map([
  [featureFlags.useVueRouter],
]);

/**
 * The Feature Flags Module is meant to replace using the SettingsModule when querying
 * feature flags. Currently it gets feature flags from Mongo via the SettingsModule, and then
 * also queries LaunchDarkly for feature flags. LaunchDarkly will be replacing Mongo for the feature
 * flag store in the near term. But both sources are currently supported.
 *
 * The Feature Flags Module _will_ override Mongo flags with LaunchDarkly flags when a record
 * exists in the featureFlagToProviderMap. For example, a "featureName" flag from
 * Mongo is used to toggle user experience.  However, a record exists in the
 * featureFlagToProviderMap, mapping a LaunchDarkly feature flag to
 * the name of a feature flag found in Mongo, so the value from LaunchDarkly will _replace_ the
 * value from Mongo.
 *
 * This functionality has two purposes:
 *
 * 1. It allows us to replace existing Mongo feature flags with LaunchDarkly feature flags.
 * 2. It allows us to use more succinct names for feature flags in client side code, e.g.
 *    "featureName" instead of "YYYY-MM-DD-feature-name".
 */
export function createFeatureFlagsModule(config = {}) {
  const {
    LaunchDarklyClient = LDClient,
    configService = ConfigService,
    localStorageService = LocalStorageService,
  } = config;

  return {
    namespaced: true,

    state: {
      betaFeatures: {},
    },

    actions: {
      /**
       * Important: this requires SettingsModule/fetchUserSettings and
       * SettingsModule/fetchSettings to happen first in order to get the userId and orgId.
       */
      async fetchFeatureFlags({ dispatch }) {
        dispatch('getBetaFeatures');

        if (configService.launchDarklySource === 'localStorage') {
          dispatch('initLocalStorageClient');
        } else {
          await dispatch('initLaunchDarklyClient');
        }
      },

      /**
       * @todo Consider calling SettingsApi.getGlobalSettings() to fetch our own copy of the beta
       * features _and_ not make this module depend on the SettingsModule to get the beta
       * features from Mongo.
       */
      getBetaFeatures({ commit, rootState }) {
        const { betaFeatures } = rootState[featureFlagsModule];

        if (betaFeatures) {
          Object.entries(betaFeatures).forEach(([key, value]) => {
            commit('UPDATE_BETA_FEATURES', { [key]: value });
          });

          // SI also rerturns feature flags from LaunchDarkly so those values are mapped here
          // first. The LaunchDarkly client will only push _changes_ to feature flags to us so the
          // initial values from SI should be mapped, and honored, first. If these feature flags
          // change in the current session then the change handler registered below in the
          // initLaunchDarklyClient action will be called to update them.

          Object.entries(featureFlagToProviderMap).forEach(([featureFlag, ldFlagName]) => {
            if (betaFeatures[ldFlagName] !== undefined) {
              commit('UPDATE_BETA_FEATURES', { [featureFlag]: betaFeatures[ldFlagName] });
            }
          });
        }
      },

      async initLaunchDarklyClient({ commit, rootState }) {
        let ldClient;

        const processLaunchDarklySettings = (settings) => {
          Object.keys(featureFlagToProviderMap).forEach((key) => {
            const flag = featureFlagToProviderMap[key];
            const value = settings
              ? settings[flag] && settings[flag].current
              : ldClient.variation(flag, undefined);

            if (value !== undefined) {
              commit('UPDATE_BETA_FEATURES', { [key]: value });
            }
          });
        };

        return new Promise((resolve) => {
          const { launchDarklyClientSideId, launchDarklyBaseUrls } = configService;
          const { orgId, user } = rootState[featureFlagsModule];

          const id = user && user.provider
            ? user.provider // We're not likely to find either a user or an orgId, but we could.
            : orgId || localStorageService.getValue(localStorageOrgKey) || 'anonymous';

          const key = shajs('sha256').update(String(id)).digest('hex');
          const payload = { key };

          const options = {};
          if (!isEmpty(launchDarklyBaseUrls)) {
            Object.entries(launchDarklyBaseUrls).forEach(([optionKey, optionValue]) => {
              // Only set the config values if an explicit value was given,
              // so that the default value is controlled by the LD SDK and not us.
              if (optionValue !== undefined && optionValue !== null) {
                options[optionKey] = optionValue;
              }
            });
          }

          ldClient = LaunchDarklyClient.initialize(launchDarklyClientSideId, payload, options);

          ldClient.on('ready', () => {
            processLaunchDarklySettings();
            ldClient.on('change', settings => processLaunchDarklySettings(settings));
            resolve();
          });
        });
      },

      /**
       * When the launchDarklySource field of the GET /ui-settings.json response is set to
       * localStorage then feature flags will be read from local storage.
       *
       * Follow the steps below to use local storage as the feature flag source.
       *
       * 1. Edit /env/ui-settings.json in jumpcloud-workstation
       *
       *    diff --git a/env/ui-settings.json b/env/ui-settings.json
       *    index df424e6..5a4ad50 100644
       *    --- a/env/ui-settings.json
       *    +++ b/env/ui-settings.json
       *    @@ -2,6 +2,7 @@
       *       "deviceCertAuthCodeUrl": "https://device-cert.jumpcloud.local/auth/code",
       *       "driftScriptUrl": "https://thirdparty.jumpcloud.com/js/drift-stg.min.js",
       *       "launchDarklyClientSideId": "5eb98a3db092960a8897c0f9",
       *    +  "launchDarklySource": "localStorage",
       *       "segmentScriptUrl": "https://thirdparty.jumpcloud.com/js/segment-local.min.js",
       *       "signupFbConfigUrl": "https://thirdparty.jumpcloud.com/js/fb-config-stg.min.js",
       *       "signupFbEventsUrl": "https://thirdparty.jumpcloud.com/js/fb-events-stg.min.js",
       *
       * 2. Stop and start all workstation services.
       *
       * 3. Navigate to https://console.jumpcloud.local
       *
       * 4. Write feature flags to local storage:
       *    var adminFlowFlag = '2021-05-21-confirm-totp-on-admin-reset-pwd-workflow';
       *    window.localStorage.setItem(adminFlowFlag, true);
       */
      initLocalStorageClient({ commit }) {
        const setFeatureFlags = () => {
          try {
            Object.entries(featureFlagToProviderMap).forEach(([key, flag]) => {
              let value = localStorageService.getValue(flag);

              // If the feature flag was not found in local storage then check for a default value.
              // Default values are provided when working with local storage as the feature flag
              // source so that flags that have targeting enabled and serve a default value in LD
              // have the same functionality when working with local storage.

              if (value === null) {
                value = featureFlagDefaultValues[key];
              }

              if (typeof value === 'boolean') {
                commit('UPDATE_BETA_FEATURES', { [key]: value });
              }
            });
          } catch (exp) {
            // eslint-disable-next-line
            console.error(`FeatureFlagsModule.initLocalStorageClient(): Error reading feature flags from localStorage = ${exp.toString()}`);
          }
        };

        setFeatureFlags();

        // NOTE: The storage event _must_ be triggered in another document (i.e. a new tab). The
        // event will not fire when the change to localStorage is initiated in the document where
        // the event listener was added.
        // SEE: https://developer.mozilla.org/en-US/docs/Web/API/Window/storage_event
        // pageNavigationUtil.addEventListener(EventConsts.storage, setFeatureFlags);
      },


      resetFeatureFlags({ commit }) {
        commit('RESET_FEATURE_FLAGS');
      },
    },

    getters: {
      betaFeatures(state) {
        return state.betaFeatures;
      },
    },

    mutations: {
      UPDATE_BETA_FEATURES(state, payload) {
        Object.keys(payload).forEach((key) => {
          if (singleSetFeatureFlags.has(key) && state.betaFeatures[key] !== undefined) {
            return;
          }
          Vue.set(state.betaFeatures, key, payload[key]);
        });
      },

      RESET_FEATURE_FLAGS(state) {
        Vue.set(state, 'betaFeatures', {});
      },
    },
  };
}

export default createFeatureFlagsModule();
